/**
 * A smart Link component that takes care of different APIs. Prismic link API gives us
 * totally different data structures depending on how you fetch it (Rest vs GraphQL),
 * where you find it (link field vs rich text), and where it points to (web, doc, file, or image)
 *
 * Copyright (c) 2020 Propellant Software Corp
 * Author: Yucho Ho
 */
import React from 'react'
import PropTypes from 'prop-types'
import { Link as GatsbyLink, withPrefix, graphql } from 'gatsby'
import { Link as PrismicLink } from 'prismic-reactjs'
import cx from 'classnames'
import { linkResolver } from 'src/utils/linkResolver'

export const pathPrefix = withPrefix('/')

export const propTypes = {
  to: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  children: PropTypes.node
}

export const defaultProps = {
  to: ''
}

export const query = graphql`
  fragment LinkFragment on PRISMIC__Linkable {
    #----- Linkables -----

    ... on PRISMIC__ExternalLink {
      _linkType
      url
    }
    ... on PRISMIC__FileLink {
      _linkType
      url
    }
    ... on PRISMIC__ImageLink {
      _linkType
      url
    }

    #----- Documents -----

    ... on PRISMIC_Legal {
      _linkType
      _meta {
        type
        uid
      }
    }
    ... on PRISMIC_Static {
      _linkType
      _meta {
        type
        uid
      }
    }
  }
`

/**
 * Creates a hyperlink for prismic link API. If props.to:
 * 1. is nullish, return span tag
 * 2. points to document type, return gatsby Link component
 * 3. starts with pathPrefix, return gatsby Link component
 * 4. else, treat it like an external link and return anchor tag
 * @param {props} props The props
 */
const Link = ({ to, children, ...attrs }) => {
  if (!to) {
    const spanClass = cx(attrs.className, 'invalid-link')
    return (
      <span {...attrs} className={spanClass}>
        {children}
      </span>
    )
  }

  let url
  // Convert prismic link data to string url
  if (typeof to === 'object') {
    // case: prismic document-link API from graphQL endpoint
    if (to._linkType === 'Link.document') {
      url = linkResolver(to._meta)
    }
    // case: prismic (web/image/file)-link API from REST endpoint OR prismic rich-text body
    else if (to.link_type) {
      url = PrismicLink.url(to, linkResolver)
    }
    // case: prismic (web/image/file)-link API from graphQL endpoint
    else {
      url = to.url
    }
  } else {
    // props.to is a string url
    url = to
  }

  // Prismic automatically prepends https:// to all external links
  // Remove it if we just want an internal link
  if (url.startsWith('https:///')) {
    url = url.slice(8)
  }

  // If external link is in fact pointing to static pages, remove path prefix
  // This is necessary because Gatsby Link component also prepends path prefix, which
  // would otherwise result in duplicate path prefix
  if (to._linkType === 'Link.web' || to.link_type === 'Web') {
    const isStaticPath = url.startsWith(`${pathPrefix}/`)

    if (isStaticPath) {
      // If pathPrefix is "/", don't remove it
      const prefixLength = pathPrefix.length > 1 ? pathPrefix.length : 0

      const linkProps = {
        to: url.slice(prefixLength),
        ...attrs
      }

      return <GatsbyLink {...linkProps}>{children}</GatsbyLink>
    }
  }

  if (to._linkType === 'Link.document' || to.link_type === 'Document') {
    const linkProps = {
      to: url,
      ...attrs
    }

    return <GatsbyLink {...linkProps}>{children}</GatsbyLink>
  }

  const anchorProps = {
    href: url,
    ...attrs
  }

  return <a {...anchorProps}>{children}</a>
}

Link.propTypes = propTypes
Link.defaultProps = defaultProps

export default Link
